import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  EmployeeContract,
  EmployeeContractCalculated
} from './employee-contract'
import { EmployeeContractSerializer } from './employee-contract.serializer'
import {
  EmployeeBooking,
  EmployeeBookingTypes
} from '../employee-booking/employee-booking'
import moment from 'moment-mini'
import { Product } from '../../product/product'
import { Dictionary } from '@ngrx/entity'
import { groupBy } from 'src/app/shared/utils'
import { EmployeeContractManagerLink } from '../employee-contract-manager-link/employee-contract-manager-link'
import { Member } from '../../member/member'
import { EmployeeBookingService } from '../employee-booking/employee-booking.service'

@Injectable()
export class EmployeeContractService extends ResourceService<EmployeeContract> {
  public employeeBookingTypes = EmployeeBookingTypes

  constructor(
    private http: HttpClient,
    private employeeContractSerializer: EmployeeContractSerializer,
    private employeeBookingService: EmployeeBookingService
  ) {
    super(
      http,
      `${environment.backendApiUrl}/employee-contracts`,
      employeeContractSerializer
    )
  }

  public calculateContractHours(
    contract: EmployeeContract,
    employeeBookings: EmployeeBooking[],
    productEntities: Dictionary<Product>
  ): EmployeeContractCalculated {
    let contractCalculated: EmployeeContractCalculated = {
      plusMinusMinutes: 0,
      netMinutes: 0,
      durationInMinutes: 0,
      employeeTaskTime: 0,
      leaveMinutesWithoutPrepTime: 0,
      leaveMinutesWithPrepTime: 0,
      sickLeaveMinutesWithoutPrepTime: 0,
      sickLeaveMinutesWithPrepTime: 0,
      specialLeaveMinutesWithoutPrepTime: 0,
      specialLeaveMinutesWithPrepTime: 0,
      workedMinutesWithoutPrepTime: 0,
      workedMinutesWithPrepTime: 0,
      scheduledTaskDays: 0,
      scheduledMinutesWithoutPrepTime: 0,
      scheduledMinutesWithPrepTime: 0,
      preparationMinutes: 0
    }

    if (!employeeBookings?.length) {
      return contractCalculated
    }

    const netMinutes =
      (contract.contractHours -
        (contract.vacationHours + contract.nationalHolidayHours)) *
      60

    const workedMinutesWithoutPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.CONTRACT
      ),
      productEntities,
      false
    )

    const workedMinutesWithPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.CONTRACT
      ),
      productEntities,
      true
    )

    const leaveMinutesWithoutPrepTime = this.getTotalMinutes(
      employeeBookings.filter((b) => b.type == this.employeeBookingTypes.LEAVE),
      productEntities,
      false
    )

    const leaveMinutesWithPrepTime = this.getTotalMinutes(
      employeeBookings.filter((b) => b.type == this.employeeBookingTypes.LEAVE),
      productEntities,
      true
    )

    const sickLeaveMinutesWithoutPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.SICK_LEAVE
      ),
      productEntities,
      false
    )

    const sickLeaveMinutesWithPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.SICK_LEAVE
      ),
      productEntities,
      true
    )

    const specialLeaveMinutesWithoutPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.SPECIAL_LEAVE
      ),
      productEntities,
      false
    )

    const specialLeaveMinutesWithPrepTime = this.getTotalMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.SPECIAL_LEAVE
      ),
      productEntities,
      true
    )

    const scheduledMinutesWithoutPrepTime = this.getTotalMinutes(
      employeeBookings,
      productEntities,
      false
    )

    const scheduledMinutesWithPrepTime = this.getTotalMinutes(
      employeeBookings,
      productEntities,
      true
    )

    const plusMinusMinutes =
      (scheduledMinutesWithPrepTime / 60 -
        leaveMinutesWithPrepTime / 60 -
        contract?.hoursWorked -
        netMinutes / 60 +
        contract?.workHoursLeftPrevPeriod) *
      60

    const scheduledTaskDays = this.getScheduledTaskDays(employeeBookings)

    const preparationMinutes = this.getPreparationMinutes(
      employeeBookings.filter(
        (b) => b.type == this.employeeBookingTypes.CONTRACT
      ),
      productEntities
    )

    return {
      ...contractCalculated,
      netMinutes,
      leaveMinutesWithoutPrepTime,
      leaveMinutesWithPrepTime,
      sickLeaveMinutesWithoutPrepTime,
      sickLeaveMinutesWithPrepTime,
      specialLeaveMinutesWithoutPrepTime,
      specialLeaveMinutesWithPrepTime,
      workedMinutesWithoutPrepTime,
      workedMinutesWithPrepTime,
      scheduledMinutesWithoutPrepTime,
      scheduledMinutesWithPrepTime,
      plusMinusMinutes,
      scheduledTaskDays,
      preparationMinutes
    }
  }

  private getTotalMinutes(
    employeeBookings: EmployeeBooking[],
    productEntities: Dictionary<Product>,
    withPrepTime: boolean = false
  ): number {
    return employeeBookings.reduce(
      (total, booking) =>
        (total += this.getBookingDurationMinutes(
          booking,
          productEntities[booking.productId],
          withPrepTime
        )),
      0
    )
  }

  private getBookingDurationMinutes(
    employeeBooking: EmployeeBooking,
    product: Product,
    withPrepTime: boolean
  ): number {
    if (!employeeBooking || !product) {
      return 0
    }

    const durationInMinutes = moment
      .duration(
        moment(employeeBooking.endDate).diff(moment(employeeBooking.startDate))
      )
      .asMinutes()

    return withPrepTime
      ? durationInMinutes +
          product.employeeTaskMinutes * (durationInMinutes / 60)
      : durationInMinutes
  }

  private getScheduledTaskDays(employeeBookings: EmployeeBooking[]): number {
    const bookingsByDate = groupBy(employeeBookings, (b) =>
      moment(b.startDate).format('YYYY-MM-DD')
    )

    return Object.keys(bookingsByDate).length
  }

  private getPreparationMinutes(
    employeeBookings: EmployeeBooking[],
    productEntities: Dictionary<Product>
  ): number {
    let prepTimeMinutes = 0

    employeeBookings.forEach((employeeBooking) => {
      const durationInMinutes = moment
        .duration(
          moment(employeeBooking.endDate).diff(
            moment(employeeBooking.startDate)
          )
        )
        .asMinutes()

      prepTimeMinutes +=
        productEntities[employeeBooking.productId]?.employeeTaskMinutes *
        (durationInMinutes / 60)
    })

    return prepTimeMinutes
  }

  public getTimeStringInHoursAndMinutes(time) {
    let decimalTimeString = time.toString()
    let decimalTime = parseFloat(decimalTimeString)
    decimalTime = decimalTime * 60 * 60
    let hours = Math.floor(decimalTime / (60 * 60))
    decimalTime = decimalTime - hours * 60 * 60
    let minutes = Math.floor(decimalTime / 60)

    let hoursString = hours.toString()
    let minutesString = minutes.toString()

    if (hours < 10) {
      hoursString = '0' + hours
    }
    if (minutes < 10) {
      minutesString = '0' + minutes
    }

    return hoursString + ':' + minutesString
  }

  public getAvailableContracts(
    employeeContracts: EmployeeContract[],
    employeeContractManagerLinkEntities: Dictionary<EmployeeContractManagerLink>,
    member: Member,
    referenceDate?: moment.Moment
  ): EmployeeContract[] {
    const filteredContracts = employeeContracts.filter((contract) => {
      if (contract?.personId != member?.id) {
        return false
      }

      const endDate = moment(contract?.endDate)

      if (
        !contract?.checkedTillDate &&
        endDate.valueOf() &&
        (referenceDate
          ? !referenceDate.isBefore(endDate)
          : endDate.clone().add(2, 'month').isBefore(moment()))
      ) {
        return false
      }

      let agendaCloseDate = this.employeeBookingService.getAgendaCloseDate(
        contract,
        employeeContractManagerLinkEntities
      )
      let lastChangedDate = null

      contract?.employeeContractManagerLinks?.forEach(
        (employeeContractManagerLinkId) => {
          if (
            moment(
              employeeContractManagerLinkEntities[employeeContractManagerLinkId]
                ?.updatedAt
            ).isAfter(moment(lastChangedDate)) ||
            !lastChangedDate
          ) {
            lastChangedDate =
              employeeContractManagerLinkEntities[employeeContractManagerLinkId]
                ?.updatedAt
          }
        }
      )

      if (!agendaCloseDate) {
        return true
      }

      if (
        moment(agendaCloseDate).valueOf() == endDate.valueOf() &&
        moment(lastChangedDate).clone().add(2, 'month').isBefore(moment())
      ) {
        return false
      }

      return true
    })

    return filteredContracts
  }
}
